/**
 * Shared constants — single source of truth for enum-like string unions
 * used across the API, workers, and frontend.
 */

export const ROLES = [
  "visitor",
  "premium",
  "circle",
  "chapter",
  "chapter_leader",
  "executive",
  "admin",
] as const;
export type Role = (typeof ROLES)[number];

// Rank is used by RBAC middleware. Higher number = more access.
export const ROLE_RANK: Record<Role, number> = {
  visitor: 0,
  premium: 10,
  circle: 20,
  chapter: 30,
  chapter_leader: 40,
  executive: 80,
  admin: 100,
};

export const PILLARS = ["mind", "body", "soul", "lifestyle"] as const;
export type Pillar = (typeof PILLARS)[number];

export const CATEGORIES = [
  "identity",
  "brotherhood",
  "sisterhood",
  "wellness",
  "faith",
  "entrepreneurship",
  "workforce",
  "policy",
  "leadership",
  "economic_mobility",
  "technology",
  "education",
  "athlete_development",
  "investments",
  "grants",
  "culture",
] as const;
export type Category = (typeof CATEGORIES)[number];

export const BRIEFING_KINDS = [
  "daily",
  "executive",
  "city",
  "founder",
  "chapter",
  "opportunity",
  "economic",
] as const;
export type BriefingKind = (typeof BRIEFING_KINDS)[number];

export const OPPORTUNITY_KINDS = [
  "grant",
  "fellowship",
  "job",
  "scholarship",
  "accelerator",
  "partnership",
  "funding",
  "event",
  "speaking",
] as const;
export type OpportunityKind = (typeof OPPORTUNITY_KINDS)[number];

export const SAVED_OPPORTUNITY_STATUS = ["saved", "pursuing", "applied", "won", "passed"] as const;
export type SavedOpportunityStatus = (typeof SAVED_OPPORTUNITY_STATUS)[number];

export const FOLLOWED_TOPIC_KINDS = [
  "opportunity_kind",
  "category",
  "geo",
  "entity",
] as const;
export type FollowedTopicKind = (typeof FOLLOWED_TOPIC_KINDS)[number];

export const CIRCLE_KINDS = [
  "brotherhood",
  "sisterhood",
  "leadership",
  "founder",
  "athlete",
  "faith",
  "wellness",
] as const;
export type CircleKind = (typeof CIRCLE_KINDS)[number];

export const CIRCLE_MEMBER_ROLES = ["member", "leader", "moderator"] as const;
export type CircleMemberRole = (typeof CIRCLE_MEMBER_ROLES)[number];

export const CIRCLE_MEMBER_STATUS = ["active", "pending"] as const;
export type CircleMemberStatus = (typeof CIRCLE_MEMBER_STATUS)[number];

export const CIRCLE_STATUS = ["active", "draft", "archived"] as const;
export type CircleStatus = (typeof CIRCLE_STATUS)[number];

export const CIRCLE_PROMPT_CATEGORIES = [
  "reflection",
  "leadership",
  "wellness",
  "opportunity",
  "faith",
  "accountability",
] as const;
export type CirclePromptCategory = (typeof CIRCLE_PROMPT_CATEGORIES)[number];

export const CIRCLE_CADENCES = ["weekly", "biweekly", "monthly"] as const;
export type CircleCadence = (typeof CIRCLE_CADENCES)[number];

export const ITEM_STATUS = ["pending", "synthesized", "failed", "skipped"] as const;
export type ItemStatus = (typeof ITEM_STATUS)[number];

export const PROMPT_VERSION = "v1.0.0";

// Analytics events — kept as a strict enum so the client can't emit
// arbitrary names. Props are categorical only (no PII, no identifiers).
export const ANALYTICS_EVENTS = [
  "dashboard.view",
  "brief.generate",
  "intelligence.save",
  "intelligence.unsave",
  "follow.add",
  "circle.join",
  "opportunity.save",
  "opportunity.dismiss",
  "reflection.save",
  "collection.create",
  "collection.entry.add",
  "collection.entry.revisit",
  "collection.entry.archive",
] as const;
export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[number];

export const COLLECTION_ENTRY_KINDS = ["intelligence", "opportunity", "briefing"] as const;
export type CollectionEntryKind = (typeof COLLECTION_ENTRY_KINDS)[number];

export const COLLECTION_ENTRY_STATES = ["unread", "revisiting", "archived"] as const;
export type CollectionEntryState = (typeof COLLECTION_ENTRY_STATES)[number];

/**
 * Suggested collection names surfaced as one-tap quick-adds when a user
 * has none. Not predefined rows — users can ignore these entirely and
 * create their own custom collections.
 */
export const SUGGESTED_COLLECTIONS: ReadonlyArray<{ name: string; slug: string }> = [
  { name: "Leadership", slug: "leadership" },
  { name: "Wellness", slug: "wellness" },
  { name: "Funding", slug: "funding" },
  { name: "Entrepreneurship", slug: "entrepreneurship" },
  { name: "Faith", slug: "faith" },
  { name: "Policy", slug: "policy" },
];

// Phase 3 — Evening Reflection prompt pool.
// 16 calm, identity-centered prompts. Rotation is deterministic by
// day-of-year so every user sees the same prompt on the same day; this
// makes "today's reflection" a shared anchor without being a feed.
export const REFLECTION_CATEGORIES = [
  "reflection",
  "leadership",
  "wellness",
  "purpose",
] as const;
export type ReflectionCategory = (typeof REFLECTION_CATEGORIES)[number];

export interface ReflectionPrompt {
  category: ReflectionCategory;
  text: string;
}

export const REFLECTION_PROMPTS: ReadonlyArray<ReflectionPrompt> = [
  // reflection
  { category: "reflection", text: "What shifted today?" },
  { category: "reflection", text: "What's still unresolved as you close the day?" },
  { category: "reflection", text: "What's worth carrying into tomorrow?" },
  { category: "reflection", text: "What did you learn today that you didn't know yesterday?" },

  // leadership
  { category: "leadership", text: "Where did you make space for someone else to lead today?" },
  { category: "leadership", text: "What truth did you avoid saying today?" },
  { category: "leadership", text: "Who needs your attention more than your answer?" },
  { category: "leadership", text: "What would you do differently if you led from your values, not your fear?" },

  // wellness
  { category: "wellness", text: "What does your body need that you've been ignoring?" },
  { category: "wellness", text: "Where did you push through when slowing down was wiser?" },
  { category: "wellness", text: "What restored you today, even briefly?" },
  { category: "wellness", text: "How present were you in the conversations that mattered?" },

  // purpose
  { category: "purpose", text: "Did today move you toward what matters most?" },
  { category: "purpose", text: "What would your future self thank you for from today?" },
  { category: "purpose", text: "What did you do today that only you could do?" },
  { category: "purpose", text: "Where is your time betraying your stated priorities?" },
];

/**
 * Deterministic daily prompt selector. Pure function of the UTC date — every
 * user sees the same prompt on the same calendar day. Use the date the user
 * is reflecting ON (their local evening), not server-side now.
 */
export function reflectionPromptFor(dayKey: string): ReflectionPrompt {
  // dayKey: "YYYY-MM-DD"
  let h = 5381;
  for (let i = 0; i < dayKey.length; i++) {
    h = ((h << 5) + h + dayKey.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % REFLECTION_PROMPTS.length;
  return REFLECTION_PROMPTS[idx]!;
}
